import { AppHomeScreen } from "@components/brand/AppHomeScreen";
import { PhoneFrame } from "@components/brand/PhoneFrame";
import { ValueLine } from "@components/brand/ValueLine";
import { StoreBadges } from "@components/shared/StoreBadges";
import { UNLOCK } from "@/lib/site";
import { track } from "@services/analytics";
import { useTranslations } from "@services/strings";
import { ShieldCheck } from "lucide-react";

/**
 * Home hero. Left: the slogan and the three numbers that matter. Right: the
 * app's home screen in a device, with the property-value line rising behind
 * it — the one animated moment on the page.
 */
export const Hero = () => {
  const { t, formatNumber } = useTranslations();

  return (
    <section className="relative overflow-hidden bg-white">
      <ValueLine className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] w-full rtl:-scale-x-100 md:h-[65%]" />
      <div className="container relative grid items-center gap-12 py-14 md:grid-cols-[1.1fr_0.9fr] md:gap-8 md:py-24">
        <div className="relative z-10 max-w-xl">
          <h1 className="display text-display-sm md:text-display">
            {t("site.home.hero.title")}
          </h1>
          <p className="mt-6 max-w-prose text-body text-slate-600 md:text-lg md:leading-relaxed">
            {t("site.home.hero.subtitle", {
              maxAmount: formatNumber(UNLOCK.maxAmount),
              maxYears: UNLOCK.maxYears,
              approvalDays: UNLOCK.approvalDays,
            })}
          </p>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
            <StoreBadges placement="hero" />
            <a
              href="#estimator"
              onClick={() =>
                track({
                  event: "Button Clicked",
                  properties: { label: "Hero - See what you could unlock" },
                })
              }
              className="inline-flex h-11 items-center justify-center rounded-full border border-slate-300 px-5 text-[15px] font-semibold text-ink transition-colors hover:border-ink"
            >
              {t("site.home.hero.cta")}
            </a>
          </div>

          <p className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck
              size={18}
              className="text-mint-ink"
              aria-hidden="true"
            />
            {t("site.home.hero.licensed")}
          </p>
        </div>

        <div className="relative flex justify-center md:justify-end">
          <PhoneFrame className="relative">
            <AppHomeScreen />
          </PhoneFrame>
        </div>
      </div>
    </section>
  );
};
